import type { Contract } from '@gmn-portal/backend';
import { useState } from 'react';
import { toast } from 'sonner';
import { useCopyContract } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Label,
} from '@/shared/components/ui';
import { DatePicker } from '@/shared/components/ui/date-picker';

interface CopyContractModalProps {
  contract: Contract | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CopyContractModal({ contract, open, onOpenChange }: CopyContractModalProps) {
  const { t } = useIntl();
  const [newStartDate, setNewStartDate] = useState<Date | undefined>(undefined);
  const copyContract = useCopyContract();

  const handleCopyContract = async () => {
    if (!contract) return;

    try {
      await copyContract.mutateAsync({
        id: contract.id,
        newStartDate: newStartDate?.toISOString(),
      });
      toast.success(t('contracts.contractCopiedSuccessfully'));
      onOpenChange(false);
      setNewStartDate(undefined);
    } catch (_error) {
      toast.error(t('contracts.errorCopyingContract'));
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setNewStartDate(undefined);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('contracts.copyContract')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('contracts.copyContractAndSetNewStartDate')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="newStartDate">{t('contracts.newStartDate')}</Label>
            <DatePicker
              date={newStartDate}
              setDate={(date) => setNewStartDate(date || undefined)}
              placeholder={t('contracts.chooseStartDate')}
            />
            <p className="text-sm text-muted-foreground">
              {t('contracts.copyContractDateDescription')}
            </p>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={handleCopyContract} disabled={copyContract.isPending}>
            {copyContract.isPending ? t('contracts.copyingContract') : t('contracts.copyContract')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
